import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { ShipmentOrder } from '@core/models/job-file/shipment-order.model';

@Component({
  selector: 'hmt-job-plan-order-list-items',
  templateUrl: './job-plan-order-list-items.component.html',
  styleUrls: ['./job-plan-order-list-items.component.scss'],
  standalone: false
})
export class JobPlanOrderListItemsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() set orders(value: ShipmentOrder[]) {
    // Filter out orders with show === false
    this._orders = (value || []).filter(order => order.show !== false);
    // Store original quantities when orders are set
    // Use static map to persist across component destruction/recreation
    this._orders.forEach(order => {
      if (order.itemRefId) {
        // Only store original quantity if we don't already have one stored
        // This ensures we preserve the true original quantity even if the component
        // is destroyed and recreated with a modified order.quantity value
        if (!JobPlanOrderListItemsComponent.originalQuantitiesMap.has(order.itemRefId)) {
          const currentQuantity = order.quantity || 0;
          // Store the original quantity - this will persist across component instances
          JobPlanOrderListItemsComponent.originalQuantitiesMap.set(order.itemRefId, currentQuantity);
        }
        // If we already have an original quantity stored, don't overwrite it
        // This prevents modified quantities from becoming the new "original"
      }
    });
  }
  get orders(): ShipmentOrder[] {
    return this._orders;
  }
  private _orders: ShipmentOrder[] = [];

  @Input() orderSelected: boolean = false;
  @Input() hideCheckboxes: boolean = false;
  @Output() selectOrderItem = new EventEmitter<{ selected: boolean; order: ShipmentOrder }>();
  @Output() selectionChanged = new EventEmitter<{ selectedCount: number; totalCount: number }>();
  @Output() quantityChanged = new EventEmitter<{ order: ShipmentOrder; newQuantity: number }>();

  displayedColumns: string[] = [
    'select',
    'sku',
    'itemName',
    'qty',
    'dimensions',
    'netWeight',
    'grossWeight',
    'cbm',
    'dangerous',
    'temperature',
    'fragile'
  ];

  selectedOrders: ShipmentOrder[] = [];
  // Map to track quantity changes: itemRefId -> newQuantity
  private quantityChanges: Map<string, number> = new Map();
  // Static map to store original quantities across component instances: itemRefId -> originalQuantity
  // This ensures original quantities persist even when component is destroyed and recreated
  private static originalQuantitiesMap: Map<string, number> = new Map();

  get visibleColumns(): string[] {
    if (this.hideCheckboxes) {
      return this.displayedColumns.filter(col => col !== 'select');
    }
    return this.displayedColumns;
  }

  constructor() {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    // When order becomes selected, select all items
    if (changes['orderSelected'] && !changes['orderSelected'].firstChange) {
      const currentValue = changes['orderSelected'].currentValue;
      const previousValue = changes['orderSelected'].previousValue;
      
      // Defer state changes to next change detection cycle to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        if (currentValue && !previousValue) {
          // Order just became selected - select all items if not already all selected
          const allItemsAlreadySelected = this.selectedOrders.length === this.orders.length;
          
          if (!allItemsAlreadySelected) {
            // Select all items if not already all selected
            this.selectAllItems();
          }
        } else if (!currentValue && previousValue) {
          // Order just became unselected
          // Don't automatically deselect items here because:
          // 1. If order becomes partially selected (some items still selected), we want to keep those selections
          // 2. If order is explicitly unselected via checkbox, the parent will call deselectAllItems() directly
          // This prevents clearing selections when order transitions from fully selected to partially selected
        }
      }, 0);
    }
    
    // Also handle initial state when component is created with order already selected
    if (changes['orderSelected']?.firstChange && this.orderSelected) {
      // Defer to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        // If order is already selected when component is created, select all items
        this.selectAllItems();
      }, 0);
    }
  }

  ngOnDestroy(): void {}

  getGrossWeight(order: ShipmentOrder): number {
    return (order.quantity || 0) * (order.weight || 0);
  }

  getCBM(order: ShipmentOrder): number {
    return (order.quantity || 0) * (order.cbm || 0);
  }

  getTemperature(order: any): string {
    if (order.temperatureRange && order.temperatureRange.min !== undefined && order.temperatureRange.max !== undefined) {
      return `${order.temperatureRange.min}-${order.temperatureRange.max}`;
    }
    return '-';
  }

  formatBoolean(value: boolean | undefined | null): string {
    if (value === undefined || value === null) {
      return '-';
    }
    return value ? 'Yes' : 'No';
  }

  getDangerous(order: any): string {
    return this.formatBoolean(order.dangerous);
  }

  getFragile(order: any): string {
    return this.formatBoolean(order.fragile);
  }

  isSelected(order: ShipmentOrder): boolean {
    return this.selectedOrders.some(selected => this.isSameOrder(selected, order));
  }

  toggleSelection(order: ShipmentOrder, event: any): void {
    const isChecked = event.checked;
    
    if (isChecked) {
      this.selectedOrders.push(order);
    } else {
      this.selectedOrders = this.selectedOrders.filter(selected => !this.isSameOrder(selected, order));
    }
    
    this.selectOrderItem.emit({ selected: isChecked, order });
    this.emitSelectionChanged();
  }

  private isSameOrder(order1: ShipmentOrder, order2: ShipmentOrder): boolean {
    // Compare by itemRefId as unique identifier
    return order1.itemRefId === order2.itemRefId;
  }

  toggleSelectAll(event: any): void {
    const isChecked = event.checked;
    
    if (isChecked) {
      this.selectedOrders = [...this.orders];
      this.orders.forEach(order => {
        this.selectOrderItem.emit({ selected: true, order });
      });
    } else {
      this.orders.forEach(order => {
        this.selectOrderItem.emit({ selected: false, order });
      });
      this.selectedOrders = [];
    }
    this.emitSelectionChanged();
  }

  isAllSelected(): boolean {
    return this.orders.length > 0 && this.selectedOrders.length === this.orders.length;
  }

  isIndeterminate(): boolean {
    return this.selectedOrders.length > 0 && this.selectedOrders.length < this.orders.length;
  }

  selectAllItems(): void {
    this.selectedOrders = [...this.orders];
    this.orders.forEach(order => {
      this.selectOrderItem.emit({ selected: true, order });
    });
    this.emitSelectionChanged();
  }

  deselectAllItems(): void {
    this.orders.forEach(order => {
      this.selectOrderItem.emit({ selected: false, order });
    });
    this.selectedOrders = [];
    this.emitSelectionChanged();
  }

  private emitSelectionChanged(): void {
    this.selectionChanged.emit({
      selectedCount: this.selectedOrders.length,
      totalCount: this.orders.length
    });
  }

  getSelectedCount(): number {
    return this.selectedOrders.length;
  }

  getTotalCount(): number {
    return this.orders.length;
  }


  getDisplayQuantity(order: ShipmentOrder): number {
    const itemRefId = order.itemRefId;
    if (itemRefId && this.quantityChanges.has(itemRefId)) {
      return this.quantityChanges.get(itemRefId)!;
    }
    return order.quantity || 0;
  }

  decrementQuantity(order: ShipmentOrder): void {
    if (!this.hideCheckboxes) {
      return; // Only allow when in move mode
    }
    const itemRefId = order.itemRefId;
    if (!itemRefId) {
      return;
    }
    
    const currentQuantity = this.getDisplayQuantity(order);
    // Lower bound is 1
    if (currentQuantity > 1) {
      const newQuantity = currentQuantity - 1;
      this.quantityChanges.set(itemRefId, newQuantity);
      this.quantityChanged.emit({ order, newQuantity });
    }
  }

  incrementQuantity(order: ShipmentOrder): void {
    if (!this.hideCheckboxes) {
      return; // Only allow when in move mode
    }
    const itemRefId = order.itemRefId;
    if (!itemRefId) {
      return;
    }
    
    const currentQuantity = this.getDisplayQuantity(order);
    const originalQuantity = this.getOriginalQuantity(order);
    // Upper bound is the original quantity
    if (currentQuantity < originalQuantity) {
    const newQuantity = currentQuantity + 1;
    this.quantityChanges.set(itemRefId, newQuantity);
    this.quantityChanged.emit({ order, newQuantity });
    }
  }

  getQuantityButtonsEnabled(): boolean {
    return this.hideCheckboxes; // Enabled only when in move mode
  }

  canDecrement(order: ShipmentOrder): boolean {
    if (!this.hideCheckboxes) {
      return false;
    }
    const currentQuantity = this.getDisplayQuantity(order);
    return currentQuantity > 1; // Can decrement if above lower bound (1)
  }

  canIncrement(order: ShipmentOrder): boolean {
    if (!this.hideCheckboxes) {
      return false;
    }
    const currentQuantity = this.getDisplayQuantity(order);
    const originalQuantity = this.getOriginalQuantity(order);
    return currentQuantity < originalQuantity; // Can increment if below upper bound (original quantity)
  }

  getOriginalQuantity(order: ShipmentOrder): number {
    const itemRefId = order.itemRefId;
    if (itemRefId && JobPlanOrderListItemsComponent.originalQuantitiesMap.has(itemRefId)) {
      return JobPlanOrderListItemsComponent.originalQuantitiesMap.get(itemRefId)!;
    }
    // Fallback to order.quantity if not stored yet (shouldn't happen, but safety check)
    return order.quantity || 0;
  }

  onQuantityInputChange(order: ShipmentOrder, event: Event): void {
    if (!this.hideCheckboxes) {
      return; // Only allow when in move mode
    }
    
    const input = event.target as HTMLInputElement;
    let value = input.value.trim();
    
    // Remove any non-numeric characters
    value = value.replace(/[^0-9]/g, '');
    
    // If empty, set to 1 (minimum)
    if (value === '' || value === '0') {
      value = '1';
    }
    
    const numValue = parseInt(value, 10);
    const originalQuantity = this.getOriginalQuantity(order);
    
    // Validate range: between 1 and originalQuantity
    let finalValue = numValue;
    if (numValue < 1) {
      finalValue = 1;
    } else if (numValue > originalQuantity) {
      finalValue = originalQuantity;
    }
    
    // Update the input value
    input.value = finalValue.toString();
    
    // Update the quantity changes map
    const itemRefId = order.itemRefId;
    if (itemRefId) {
      this.quantityChanges.set(itemRefId, finalValue);
      this.quantityChanged.emit({ order, newQuantity: finalValue });
    }
  }

  onQuantityInputBlur(order: ShipmentOrder, event: Event): void {
    if (!this.hideCheckboxes) {
      return; // Only allow when in move mode
    }
    
    const input = event.target as HTMLInputElement;
    let value = input.value.trim();
    
    // If empty, set to 1 (minimum)
    if (value === '' || value === '0') {
      value = '1';
    }
    
    const numValue = parseInt(value, 10);
    const originalQuantity = this.getOriginalQuantity(order);
    
    // Validate range: between 1 and originalQuantity
    let finalValue = numValue;
    if (numValue < 1) {
      finalValue = 1;
    } else if (numValue > originalQuantity) {
      finalValue = originalQuantity;
    }
    
    // Update the input value
    input.value = finalValue.toString();
    
    // Update the quantity changes map
    const itemRefId = order.itemRefId;
    if (itemRefId) {
      this.quantityChanges.set(itemRefId, finalValue);
      this.quantityChanged.emit({ order, newQuantity: finalValue });
    }
  }

  onQuantityKeyDown(event: KeyboardEvent): boolean {
    // Allow: backspace, delete, tab, escape, enter, and arrow keys
    if ([46, 8, 9, 27, 13, 37, 38, 39, 40].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true)) {
      return true;
    }
    // Ensure that it is a number and stop the keypress
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
      return false;
    }
    return true;
  }
}

